import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { userApi, workerApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExcelUploadMenu from '../../components/common/ExcelUploadMenu';
import UserManagementAuditLog from './UserManagementAuditLog';

// Bold + font size only, by request — not a full formatting toolbar. {{name}}
// and {{link}} are typed as plain text inside the editor and substituted
// server-side, same as before; Quill just gives the surrounding text real
// HTML formatting instead of requiring hand-typed tags.
const MAIL_QUILL_MODULES = {
  toolbar: [[{ size: ['small', false, 'large', 'huge'] }], ['bold', 'italic', 'underline'], ['clean']],
};

const PAGE_SIZE = 20;

const emptyCreate = { employeeId: '', fullName: '', username: '', email: '', password: '', role: 'user', designation: '', modules: [], siteId: '' };
const emptyEdit   = { employeeId: '', fullName: '', username: '', email: '', role: 'user', designation: '', isActive: true, modules: [], siteId: '' };
const emptyReset  = { newPassword: '' };
const emptyContractCreate = { employeeId: '', name: '', email: '', role: '' };
const emptyContractEdit   = { employeeId: '', name: '', email: '', role: '' };

export default function UserManagement() {
  const { user: me } = useAuth();
  const [pageTab, setPageTab] = useState('sptl');

  // ── SPTL state ───────────────────────────────────────────────────────────
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);

  const [createOpen, setCreateOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [confirm, setConfirm]         = useState(null);
  const [createForm, setCreateForm]   = useState(emptyCreate);
  const [editForm, setEditForm]       = useState(emptyEdit);
  const [resetForm, setResetForm]     = useState(emptyReset);
  const [error, setError]             = useState('');
  const fileInputRef  = useRef(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult]   = useState(null);

  const [grantOpen, setGrantOpen]       = useState(false);
  const [grantModule, setGrantModule]   = useState('');
  const [grantConfirm, setGrantConfirm] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantResult, setGrantResult]   = useState(null);

  const [mailModeChoiceOpen, setMailModeChoiceOpen] = useState(false);
  const [mailMode, setMailMode] = useState('setup'); // 'setup' | 'all'
  const [mailPreviewOpen, setMailPreviewOpen] = useState(false);
  const [mailPreviewLoading, setMailPreviewLoading] = useState(false);
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody]       = useState('');
  const [mailCc, setMailCc]           = useState('');
  const [mailTestLoading, setMailTestLoading] = useState(false);
  const [mailTestResult, setMailTestResult]   = useState('');
  const [mailConfirm, setMailConfirm] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailResult, setMailResult]   = useState(null);
  const [mailProgress, setMailProgress] = useState(null); // { total, processed, sent, failed, done }

  // ── Contract state ───────────────────────────────────────────────────────
  const [contractWorkers, setContractWorkers]       = useState([]);
  const [contractLoading, setContractLoading]       = useState(false);
  const [contractSearch, setContractSearch]         = useState('');
  const [contractPage, setContractPage]             = useState(1);
  const [contractCreateOpen, setContractCreateOpen] = useState(false);
  const [contractEditTarget, setContractEditTarget] = useState(null);
  const [contractConfirm, setContractConfirm]       = useState(null);
  const [contractCreateForm, setContractCreateForm] = useState(emptyContractCreate);
  const [contractEditForm, setContractEditForm]     = useState(emptyContractEdit);
  const [contractError, setContractError]           = useState('');
  const contractFileInputRef = useRef(null);
  const [contractBulkLoading, setContractBulkLoading] = useState(false);
  const [contractBulkResult, setContractBulkResult]   = useState(null);
  const [contractDeleteCheck, setContractDeleteCheck] = useState(null);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const [sites, setSites] = useState([]);

  const load = async () => {
    try {
      const res = await userApi.list();
      setUsers(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
    // Separate from the users load — if MaterialWatch is unreachable, the
    // site dropdown just stays empty rather than blocking the whole page.
    try {
      const sitesRes = await userApi.listSites();
      setSites(sitesRes.data.data || []);
    } catch { /* site dropdown will just be empty */ }
  };

  const loadContract = async () => {
    setContractLoading(true);
    try {
      const res = await workerApi.list({ employeeType: 'contract' });
      setContractWorkers(res.data.data);
    } catch (err) {
      setContractError(err.response?.data?.message || 'Failed to load contract workers');
    } finally {
      setContractLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (pageTab === 'contract') loadContract(); }, [pageTab]); // eslint-disable-line

  // ── SPTL handlers ────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault(); setError('');
    try {
      await userApi.create({ ...createForm, siteId: createForm.siteId ? parseInt(createForm.siteId) : null });
      setCreateOpen(false); setCreateForm(emptyCreate); load();
    } catch (err) { setError(err.response?.data?.message || 'Error creating user'); }
  };

  const openEdit = (u) => {
    setEditForm({ employeeId: u.employeeId || '', fullName: u.fullName || '', username: u.username, email: u.email, role: u.role, designation: u.designation || '', isActive: u.isActive, modules: u.moduleAccess || [], siteId: u.siteId != null ? String(u.siteId) : '' });
    setEditTarget(u); setError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setError('');
    try {
      await userApi.update(editTarget.id, { ...editForm, siteId: editForm.siteId ? parseInt(editForm.siteId) : null });
      setEditTarget(null); load();
    } catch (err) { setError(err.response?.data?.message || 'Error updating user'); }
  };

  const handleBulkGrant = async () => {
    setGrantLoading(true); setError('');
    try {
      const res = await userApi.bulkGrantModuleAccess({ module: grantModule });
      setGrantConfirm(false); setGrantOpen(false);
      setGrantResult(res.data.data);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error granting module access');
      setGrantConfirm(false);
    } finally {
      setGrantLoading(false);
    }
  };

  const openMailPreview = async (mode) => {
    setMailMode(mode);
    setMailModeChoiceOpen(false);
    setError(''); setMailTestResult(''); setMailCc(''); setMailPreviewLoading(true); setMailPreviewOpen(true);
    try {
      const res = await userApi.getPasswordSetupEmailTemplate(mode);
      setMailSubject(res.data.data.subject);
      setMailBody(res.data.data.body);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading email template');
      setMailPreviewOpen(false);
    } finally {
      setMailPreviewLoading(false);
    }
  };

  // Subject is plain text — substitute as-is. Body is HTML from the rich
  // text editor — {{link}} becomes a real sample <a> so Bold/font-size and
  // the link all preview exactly as they'll render in the sent email.
  const renderMailPreviewText = (template) => (template || '').replaceAll('{{name}}', 'Jane Doe');
  const renderMailPreviewHtml = (bodyHtml) =>
    (bodyHtml || '')
      .replaceAll('{{name}}', 'Jane Doe')
      .replaceAll('{{link}}', '<a href="https://sptlmep.in/set-password?token=sample-token-abc123">https://sptlmep.in/set-password?token=sample-token-abc123</a>')
      // Same fix applied server-side before sending — pinning it here too so
      // the preview's paragraph spacing matches the real email exactly,
      // instead of relying on this app's own CSS resets.
      .replace(/<p(\s[^>]*)?>/gi, '<p style="margin:0 0 8px 0;">');

  const handleSendTestEmail = async () => {
    setMailTestLoading(true); setMailTestResult(''); setError('');
    try {
      const res = await userApi.sendPasswordSetupEmailTest({ mode: mailMode, subject: mailSubject, body: mailBody, cc: mailCc });
      setMailTestResult(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending test email');
    } finally {
      setMailTestLoading(false);
    }
  };

  const handleBulkSendPasswordSetupEmails = async () => {
    setMailLoading(true); setError('');
    try {
      const res = await userApi.bulkSendPasswordSetupEmails({ mode: mailMode, subject: mailSubject, body: mailBody, cc: mailCc });
      setMailConfirm(false);
      setMailPreviewOpen(false);
      setMailProgress(null);
      setMailResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending emails');
      setMailConfirm(false);
    } finally {
      setMailLoading(false);
    }
  };

  // Polls live "Sent X / Y" while a bulk send is running — starts once the
  // result modal is showing a batch that's actually sending anything, stops
  // itself once the backend reports done (or the modal closes).
  useEffect(() => {
    if (!mailResult || !mailResult.data?.willSend) return;
    let cancelled = false;
    let interval;
    const poll = async () => {
      try {
        const res = await userApi.getBulkMailProgress();
        if (cancelled) return;
        setMailProgress(res.data.data);
        if (res.data.data.done) clearInterval(interval);
      } catch { /* transient — next tick retries */ }
    };
    poll();
    interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [mailResult]);

  const handleReset = async (e) => {
    e.preventDefault(); setError('');
    try {
      await userApi.resetPassword(resetTarget.id, { newPassword: resetForm.newPassword });
      setResetTarget(null); setResetForm(emptyReset);
    } catch (err) { setError(err.response?.data?.message || 'Error resetting password'); }
  };

  const handleDownloadTemplate = async () => {
    setError('');
    try {
      const res = await userApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleDownloadData = async () => {
    setError('');
    try {
      const res = await userApi.exportData();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sptl_employees_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError(err.response?.data?.message || 'Failed to download data'); }
  };

  const handleBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBulkLoading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await userApi.bulkUpload(formData);
      setBulkResult(res.data.data);
      load();
    } catch (err) { setError(err.response?.data?.message || 'Bulk upload failed'); }
    finally { setBulkLoading(false); }
  };

  // ── Contract handlers ────────────────────────────────────────────────────
  const handleContractCreate = async (e) => {
    e.preventDefault(); setContractError('');
    try {
      await workerApi.create({ name: contractCreateForm.name, employeeId: contractCreateForm.employeeId, email: contractCreateForm.email || null, role: contractCreateForm.role || null });
      setContractCreateOpen(false); setContractCreateForm(emptyContractCreate); loadContract();
    } catch (err) { setContractError(err.response?.data?.message || 'Error creating contract worker'); }
  };

  const openContractEdit = (w) => {
    setContractEditForm({ name: w.name, employeeId: w.employeeId || '', email: w.email || '', role: w.role || '' });
    setContractEditTarget(w); setContractError('');
  };

  const handleContractEdit = async (e) => {
    e.preventDefault(); setContractError('');
    try {
      await workerApi.update(contractEditTarget.id, { name: contractEditForm.name, employeeId: contractEditForm.employeeId, email: contractEditForm.email || null, role: contractEditForm.role || null });
      setContractEditTarget(null); loadContract();
    } catch (err) { setContractError(err.response?.data?.message || 'Error updating contract worker'); }
  };

  const handleContractDownloadTemplate = async () => {
    setContractError('');
    try {
      const res = await workerApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract_workers_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setContractError(err.response?.data?.message || 'Failed to download template'); }
  };

  const handleContractDownloadData = async () => {
    setContractError('');
    try {
      const res = await workerApi.exportData('contract');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract_workers_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setContractError(err.response?.data?.message || 'Failed to download data'); }
  };

  const handleContractBulkUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setContractBulkLoading(true); setContractError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await workerApi.bulkUpload(formData);
      setContractBulkResult(res.data.data);
      loadContract();
    } catch (err) { setContractError(err.response?.data?.message || 'Bulk upload failed'); }
    finally { setContractBulkLoading(false); }
  };

  const openContractDelete = async (w) => {
    setContractError('');
    try {
      const res = await workerApi.checkDelete(w.id);
      setContractDeleteCheck({ worker: w, ...res.data.data });
    } catch (err) { setContractError(err.response?.data?.message || 'Error checking worker'); }
  };

  const handleContractDelete = async (deductAndDelete) => {
    if (!contractDeleteCheck) return;
    setContractError('');
    try {
      await workerApi.remove(contractDeleteCheck.worker.id, deductAndDelete);
      setContractDeleteCheck(null); loadContract();
    } catch (err) { setContractError(err.response?.data?.message || 'Error deleting contract worker'); }
  };

  // ── Filtered + paginated data ────────────────────────────────────────────
  const byName = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

  const q = search.toLowerCase();
  const filteredUsers = users.filter(u =>
    !q ||
    u.username.toLowerCase().includes(q) ||
    (u.fullName || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q) ||
    (u.employeeId || '').toLowerCase().includes(q)
  ).sort((a, b) => byName(a.fullName || a.username, b.fullName || b.username));
  const totalPages   = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers   = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const cq = contractSearch.toLowerCase();
  const filteredContract = contractWorkers.filter(w =>
    !cq ||
    w.name.toLowerCase().includes(cq) ||
    (w.employeeId || '').toLowerCase().includes(cq) ||
    (w.email || '').toLowerCase().includes(cq) ||
    (w.role || '').toLowerCase().includes(cq)
  ).sort((a, b) => byName(a.name, b.name));
  const contractTotalPages = Math.max(1, Math.ceil(filteredContract.length / PAGE_SIZE));
  const pagedContract      = filteredContract.slice((contractPage - 1) * PAGE_SIZE, contractPage * PAGE_SIZE);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle="Manage SPTL employees and contract workers"
        action={
          pageTab === 'sptl' ? (
            <div className="flex gap-2">
              <ExcelUploadMenu
                loading={bulkLoading}
                onDownloadTemplate={handleDownloadTemplate}
                onUploadClick={() => fileInputRef.current?.click()}
                onDownloadData={handleDownloadData}
                downloadDataLabel="Download SPTL Employees Data"
              />
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUploadChange} />
              <button className="btn-secondary" onClick={() => { setGrantModule(''); setGrantOpen(true); setError(''); }}>
                Bulk Grant Access
              </button>
              {me?.role === 'super_admin' && (
                <button className="btn-secondary" onClick={() => { setError(''); setMailModeChoiceOpen(true); }}>
                  Send Mail
                </button>
              )}
              <button className="btn-primary" onClick={() => { setCreateForm(emptyCreate); setCreateOpen(true); setError(''); }}>
                + Add SPTL Employee
              </button>
            </div>
          ) : pageTab === 'contract' ? (
            <div className="flex gap-2">
              <ExcelUploadMenu
                loading={contractBulkLoading}
                onDownloadTemplate={handleContractDownloadTemplate}
                onUploadClick={() => contractFileInputRef.current?.click()}
                onDownloadData={handleContractDownloadData}
                downloadDataLabel="Download Contract Workers Data"
              />
              <input ref={contractFileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleContractBulkUploadChange} />
              <button className="btn-primary" onClick={() => { setContractCreateForm(emptyContractCreate); setContractCreateOpen(true); setContractError(''); }}>
                + Add Contract Worker
              </button>
            </div>
          ) : null
        }
      />

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {[
          { key: 'sptl', label: 'SPTL Employees' },
          { key: 'contract', label: 'Contract Workers' },
          ...(me?.role === 'super_admin' ? [{ key: 'audit', label: 'Audit Logs' }] : []),
        ].map((t) => (
          <button key={t.key} onClick={() => setPageTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              pageTab === t.key ? 'border-indigo-600 text-indigo-700 bg-indigo-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── SPTL EMPLOYEES ── */}
      {pageTab === 'sptl' && (
        <>
          {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</p>}

          {/* Search + count */}
          <div className="flex items-center gap-3 mb-4">
            <input
              className="input max-w-xs"
              placeholder="Search name, username, email, ID…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <span className="text-xs text-gray-400">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Designation</th>
                  <th>Site</th>
                  <th>Role</th>
                  <th>Module Access</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((u, i) => (
                  <tr key={u.id}>
                    <td className="text-gray-400 text-xs w-8">{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td>
                      <p className="text-sm font-medium text-gray-900">
                        {u.fullName || u.username}
                        {u.id === me?.id && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">You</span>}
                      </p>
                      <p className="text-xs text-gray-400">@{u.username}{u.employeeId ? ` · ${u.employeeId}` : ''} · {u.email}</p>
                    </td>
                    <td className="text-sm text-gray-600">{u.designation || '—'}</td>
                    <td className="text-sm text-gray-600">{sites.find((s) => s.id === u.siteId)?.name || '—'}</td>
                    <td>
                      <StatusBadge status={u.role} />
                    </td>
                    <td>
                      {/* "All Modules" reflects actual access (100% of what exists
                          today), not role — an admin only shows this if every
                          current module has genuinely been granted to them. */}
                      {u.moduleAccess?.length > 0 && u.moduleAccess.length >= ALL_MODULES.length ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 border border-purple-200">All Modules</span>
                      ) : u.moduleAccess?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.moduleAccess.map((m) => (
                            <span key={m} className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
                              {ALL_MODULES.find((am) => am.key === m)?.label || m}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-sm">None</span>
                      )}
                    </td>
                    <td><StatusBadge status={u.isActive ? 'active' : 'inactive'} /></td>
                    <td className="w-10">
                      <ActionMenu>
                        <ActionItem onClick={() => openEdit(u)}>Edit</ActionItem>
                        <ActionItem onClick={() => { setResetTarget(u); setResetForm(emptyReset); setError(''); }}>Reset Password</ActionItem>
                        <ActionItem onClick={() => setConfirm({ id: u.id, type: 'toggle', name: u.username, active: u.isActive })}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </ActionItem>
                        {u.id !== me?.id && (
                          <ActionItem danger onClick={() => setConfirm({ id: u.id, type: 'delete', name: u.username })}>Delete</ActionItem>
                        )}
                      </ActionMenu>
                    </td>
                  </tr>
                ))}
                {pagedUsers.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-gray-400">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination page={page} total={totalPages} onChange={setPage} />

          {/* Create Modal */}
          {createOpen && (
            <Modal title="Add SPTL Employee" onClose={() => setCreateOpen(false)}>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Employee ID *"><input className="input" autoComplete="off" value={createForm.employeeId} onChange={(e) => setCreateForm({ ...createForm, employeeId: e.target.value })} placeholder="EMP001" required /></Field>
                  <Field label="Full Name"><input className="input" autoComplete="off" value={createForm.fullName} onChange={(e) => setCreateForm({ ...createForm, fullName: e.target.value })} placeholder="John Smith" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Username *"><input className="input" autoComplete="off" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} required /></Field>
                  <Field label="Email"><input className="input" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Password *"><input className="input" type="password" autoComplete="new-password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required /></Field>
                  <Field label="Role">
                    <select className="input" value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      {me?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Designation">
                    <input className="input" autoComplete="off" value={createForm.designation} onChange={(e) => setCreateForm({ ...createForm, designation: e.target.value })} placeholder="e.g. Site Engineer" />
                  </Field>
                  <Field label="Assigned Site">
                    <select className="input" value={createForm.siteId} onChange={(e) => setCreateForm({ ...createForm, siteId: e.target.value })}>
                      <option value="">— None —</option>
                      {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Module Access">
                  <ModuleCheckboxes selected={createForm.modules} toggle={(m) => setCreateForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }))} />
                </Field>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <ModalActions onCancel={() => setCreateOpen(false)} submitLabel="Create Employee" />
              </form>
            </Modal>
          )}

          {/* Edit Modal */}
          {editTarget && (
            <Modal title={`Edit — ${editTarget.username}`} onClose={() => setEditTarget(null)}>
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Employee ID *"><input className="input" value={editForm.employeeId} onChange={(e) => setEditForm({ ...editForm, employeeId: e.target.value })} placeholder="EMP001" required /></Field>
                  <Field label="Full Name"><input className="input" value={editForm.fullName} onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })} placeholder="John Smith" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Username *"><input className="input" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} required /></Field>
                  <Field label="Email"><input className="input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Role">
                    <select className="input" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      {me?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select className="input" value={editForm.isActive} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === 'true' })}>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Designation">
                    <input className="input" value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} placeholder="e.g. Site Engineer" />
                  </Field>
                  <Field label="Assigned Site">
                    <select className="input" value={editForm.siteId} onChange={(e) => setEditForm({ ...editForm, siteId: e.target.value })}>
                      <option value="">— None —</option>
                      {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Module Access">
                  <ModuleCheckboxes selected={editForm.modules} toggle={(m) => setEditForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }))} />
                </Field>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <ModalActions onCancel={() => setEditTarget(null)} submitLabel="Save Changes" />
              </form>
            </Modal>
          )}

          {/* Reset Password Modal */}
          {resetTarget && (
            <Modal title={`Reset Password — ${resetTarget.username}`} onClose={() => setResetTarget(null)}>
              <form onSubmit={handleReset} className="space-y-4">
                <Field label="New Password">
                  <input className="input" type="password" autoComplete="new-password" value={resetForm.newPassword} onChange={(e) => setResetForm({ newPassword: e.target.value })} required minLength={6} />
                </Field>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <ModalActions onCancel={() => setResetTarget(null)} submitLabel="Reset Password" danger />
              </form>
            </Modal>
          )}

          {/* Bulk Grant Module Access Modal */}
          {grantOpen && (
            <Modal title="Bulk Grant Access" onClose={() => setGrantOpen(false)}>
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Grants one module to every active SPTL Employee that doesn't already have it. Existing access to other modules is left untouched.
                </p>
                <Field label="Module">
                  <select className="input" value={grantModule} onChange={(e) => setGrantModule(e.target.value)}>
                    <option value="">— Select a module —</option>
                    {(me?.role === 'super_admin' ? ALL_MODULES : ALL_MODULES.filter((m) => (me?.moduleAccess || []).includes(m.key)))
                      .map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </Field>
                {error && <p className="text-red-600 text-sm">{error}</p>}
                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  <button className="btn-secondary" onClick={() => setGrantOpen(false)}>Cancel</button>
                  <button className="btn-primary" disabled={!grantModule} onClick={() => setGrantConfirm(true)}>
                    Grant to All
                  </button>
                </div>
              </div>
            </Modal>
          )}

          <ConfirmDialog
            open={grantConfirm}
            danger={false}
            title="Bulk Grant Access"
            message={`Grant "${ALL_MODULES.find((m) => m.key === grantModule)?.label}" access to every active SPTL Employee who doesn't already have it? This cannot be undone in bulk — access would need to be revoked one user at a time.`}
            confirmLabel={grantLoading ? 'Granting…' : 'Grant to All'}
            onConfirm={handleBulkGrant}
            onCancel={() => setGrantConfirm(false)}
          />

          {/* Bulk Grant Result Modal */}
          {grantResult && (
            <Modal title="Bulk Grant Results" onClose={() => setGrantResult(null)}>
              <div className="space-y-3">
                <div className="flex gap-4 text-sm flex-wrap">
                  <span className="text-green-700 font-medium">{grantResult.granted} granted</span>
                  <span className="text-gray-500 font-medium">{grantResult.alreadyHad} already had it</span>
                  <span className="text-gray-400">{grantResult.total} active user{grantResult.total !== 1 ? 's' : ''} total</span>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                <button className="btn-primary" onClick={() => setGrantResult(null)}>Close</button>
              </div>
            </Modal>
          )}

          {/* Mail Mode Choice Modal */}
          {mailModeChoiceOpen && (
            <Modal title="Send Mail" onClose={() => setMailModeChoiceOpen(false)}>
              <div className="space-y-3">
                <p className="text-sm text-gray-500">Who should receive this email?</p>
                <button
                  className="w-full text-left border border-gray-200 rounded-lg p-3 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                  onClick={() => openMailPreview('setup')}
                >
                  <p className="font-medium text-gray-800">Password Setup</p>
                  <p className="text-xs text-gray-500 mt-0.5">Only active users who haven't set a password yet — includes their one-time set-password link.</p>
                </button>
                <button
                  className="w-full text-left border border-gray-200 rounded-lg p-3 hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                  onClick={() => openMailPreview('all')}
                >
                  <p className="font-medium text-gray-800">All</p>
                  <p className="text-xs text-gray-500 mt-0.5">Every active SPTL Employee — a general announcement, no set-password link.</p>
                </button>
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                <button className="btn-secondary" onClick={() => setMailModeChoiceOpen(false)}>Cancel</button>
              </div>
            </Modal>
          )}

          {/* Preview & Edit Email Modal */}
          {mailPreviewOpen && (
            <Modal title={`Send Mail — ${mailMode === 'setup' ? 'Password Setup' : 'All'}`} onClose={() => setMailPreviewOpen(false)} wide>
              {mailPreviewLoading ? (
                <p className="text-sm text-gray-400 py-6 text-center">Loading template…</p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    {mailMode === 'setup' ? (
                      <>Use Bold/font size as needed. <code>{'{{name}}'}</code> and <code>{'{{link}}'}</code> are replaced per recipient with their name and their unique one-time set-password link — leave those placeholders in the text.</>
                    ) : (
                      <>Use Bold/font size as needed. <code>{'{{name}}'}</code> is replaced per recipient with their name.</>
                    )}
                  </p>
                  <Field label="Subject">
                    <input className="input" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} />
                  </Field>
                  <Field label="Cc (optional)">
                    <input
                      className="input"
                      placeholder="comma-separated email addresses, e.g. manager@sptl.in, hr@sptl.in"
                      value={mailCc}
                      onChange={(e) => setMailCc(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {mailMode === 'setup'
                        ? 'They’ll receive exactly one copy of this email (not one per recipient) — with a note in place of a link, since each recipient gets their own unique link.'
                        : 'They’ll receive exactly one copy of this email, not one per recipient.'}
                    </p>
                  </Field>
                  <Field label="Body">
                    <div className="bg-white rounded-lg border border-gray-300">
                      <ReactQuill theme="snow" value={mailBody} onChange={setMailBody} modules={MAIL_QUILL_MODULES} />
                    </div>
                  </Field>

                  <div>
                    <p className="label mb-1">Preview (sample recipient)</p>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 text-sm">
                      <p className="font-semibold text-gray-800 mb-2">{renderMailPreviewText(mailSubject)}</p>
                      <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: renderMailPreviewHtml(mailBody) }} />
                    </div>
                  </div>

                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  {mailTestResult && <p className="text-green-700 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2">{mailTestResult}</p>}

                  <div className="flex justify-between items-center gap-2 pt-2 border-t border-gray-100">
                    <button className="btn-secondary" onClick={handleSendTestEmail} disabled={mailTestLoading}>
                      {mailTestLoading ? 'Sending…' : 'Send test to myself'}
                    </button>
                    <div className="flex gap-2">
                      <button className="btn-secondary" onClick={() => setMailPreviewOpen(false)}>Cancel</button>
                      <button className="btn-primary" onClick={() => setMailConfirm(true)}>Review & Send</button>
                    </div>
                  </div>
                </div>
              )}
            </Modal>
          )}

          <ConfirmDialog
            open={mailConfirm}
            danger={false}
            title="Send Mail"
            message={
              mailMode === 'setup'
                ? "Send this email to every active user who hasn't set a password yet? Microsoft 365 throttles sending, so this runs in the background — it may take several minutes for a large batch. Check Audit Logs for progress."
                : 'Send this email to every active SPTL Employee? Microsoft 365 throttles sending, so this runs in the background — it may take several minutes for a large batch. Check Audit Logs for progress.'
            }
            confirmLabel={mailLoading ? 'Sending…' : 'Send Emails'}
            onConfirm={handleBulkSendPasswordSetupEmails}
            onCancel={() => setMailConfirm(false)}
          />

          {/* Bulk Mail Result Modal */}
          {mailResult && (
            <Modal title="Send Mail" onClose={() => { setMailResult(null); setMailProgress(null); }}>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">{mailResult.message}</p>
                <div className="flex gap-4 text-sm flex-wrap">
                  <span className="text-green-700 font-medium">{mailResult.data.willSend} to send</span>
                  <span className="text-gray-500 font-medium">{mailResult.data.skippedNoEmail} skipped (no email)</span>
                  <span className="text-gray-400">{mailResult.data.total} user{mailResult.data.total !== 1 ? 's' : ''} total</span>
                </div>
                {mailProgress && mailProgress.total > 0 && (
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-gray-700">
                        {mailProgress.done ? 'Finished' : 'Sending…'} — {mailProgress.processed} / {mailProgress.total}
                      </span>
                      <span className="text-gray-400">
                        {mailProgress.sent} sent{mailProgress.failed ? `, ${mailProgress.failed} failed` : ''}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all ${mailProgress.done ? 'bg-green-500' : 'bg-indigo-500'}`}
                        style={{ width: `${Math.round((mailProgress.processed / mailProgress.total) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                <button className="btn-primary" onClick={() => { setMailResult(null); setMailProgress(null); }}>Close</button>
              </div>
            </Modal>
          )}

          {/* Excel Upload Results Modal */}
          {bulkResult && (
            <Modal title="Excel Upload Results" onClose={() => setBulkResult(null)}>
              <div className="space-y-3">
                <div className="flex gap-4 text-sm flex-wrap">
                  <span className="text-green-700 font-medium">{bulkResult.created} created</span>
                  <span className="text-blue-700 font-medium">{bulkResult.updated || 0} designation updated</span>
                  <span className="text-gray-500 font-medium">{bulkResult.unchanged || 0} unchanged</span>
                  <span className="text-red-600 font-medium">{bulkResult.failed} failed</span>
                  <span className="text-gray-400">{bulkResult.total} total row{bulkResult.total !== 1 ? 's' : ''}</span>
                </div>
                {bulkResult.created > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    New accounts are locked with a temporary password. Use <strong>Reset Password</strong> on each account to set their initial password — they'll be required to change it on first login.
                  </p>
                )}
                {bulkResult.failedRecords?.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Row</th>
                          <th className="text-left px-3 py-2">Identifier</th>
                          <th className="text-left px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResult.failedRecords.map((f, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-3 py-1.5">{f.row}</td>
                            <td className="px-3 py-1.5">{f.identifier}</td>
                            <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                <button className="btn-primary" onClick={() => setBulkResult(null)}>Close</button>
              </div>
            </Modal>
          )}

          <ConfirmDialog
            open={!!confirm}
            title={confirm?.type === 'delete' ? 'Delete Employee' : 'Toggle Status'}
            message={confirm?.type === 'delete' ? `Permanently delete "${confirm?.name}"?` : `${confirm?.active ? 'Deactivate' : 'Activate'} user "${confirm?.name}"?`}
            onConfirm={async () => {
              try {
                if (confirm.type === 'delete') await userApi.remove(confirm.id);
                else await userApi.toggleActive(confirm.id);
                setConfirm(null); load();
              } catch (err) {
                setError(err.response?.data?.message || 'Action failed');
                setConfirm(null);
              }
            }}
            onCancel={() => setConfirm(null)}
          />
        </>
      )}

      {/* ── CONTRACT WORKERS ── */}
      {pageTab === 'contract' && (
        <>
          {contractError && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{contractError}</p>}

          <div className="flex items-center gap-3 mb-4">
            <input
              className="input max-w-xs"
              placeholder="Search name, employee ID, email, designation…"
              value={contractSearch}
              onChange={(e) => { setContractSearch(e.target.value); setContractPage(1); }}
            />
            <span className="text-xs text-gray-400">{filteredContract.length} worker{filteredContract.length !== 1 ? 's' : ''}</span>
          </div>

          {contractLoading ? <LoadingSpinner /> : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Worker</th>
                      <th>Designation</th>
                      <th>Site</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedContract.map((w, i) => (
                      <tr key={w.id}>
                        <td className="text-gray-400 text-xs w-8">{(contractPage - 1) * PAGE_SIZE + i + 1}</td>
                        <td>
                          <p className="text-sm font-medium text-gray-900">{w.name}</p>
                          <p className="text-xs text-gray-400">{w.employeeId || 'No ID'}{w.email ? ` · ${w.email}` : ''}</p>
                        </td>
                        <td className="text-sm text-gray-600">{w.role || '—'}</td>
                        <td className="text-sm text-gray-600">
                          {(w.siteAssignments || []).map((a) => a.site?.name).filter(Boolean).join(', ') || '—'}
                        </td>
                        <td><StatusBadge status={w.isActive ? 'active' : 'inactive'} /></td>
                        <td className="w-10">
                          <ActionMenu>
                            <ActionItem onClick={() => openContractEdit(w)}>Edit</ActionItem>
                            <ActionItem onClick={() => setContractConfirm({ id: w.id, type: 'toggle', name: w.name, active: w.isActive })}>
                              {w.isActive ? 'Deactivate' : 'Activate'}
                            </ActionItem>
                            <ActionItem danger onClick={() => openContractDelete(w)}>Delete</ActionItem>
                          </ActionMenu>
                        </td>
                      </tr>
                    ))}
                    {pagedContract.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-10 text-gray-400">No contract workers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={contractPage} total={contractTotalPages} onChange={setContractPage} />
            </>
          )}

          {contractCreateOpen && (
            <Modal title="Add Contract Worker" onClose={() => setContractCreateOpen(false)}>
              <form onSubmit={handleContractCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Employee ID *"><input className="input" autoComplete="off" value={contractCreateForm.employeeId} onChange={(e) => setContractCreateForm({ ...contractCreateForm, employeeId: e.target.value })} placeholder="CON001" required /></Field>
                  <Field label="Full Name *"><input className="input" autoComplete="off" value={contractCreateForm.name} onChange={(e) => setContractCreateForm({ ...contractCreateForm, name: e.target.value })} placeholder="John Smith" required /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email"><input className="input" type="email" autoComplete="off" value={contractCreateForm.email} onChange={(e) => setContractCreateForm({ ...contractCreateForm, email: e.target.value })} placeholder="name@example.com" /></Field>
                  <Field label="Designation">
                    <input className="input" value={contractCreateForm.role} onChange={(e) => setContractCreateForm({ ...contractCreateForm, role: e.target.value })} placeholder="e.g. Operator, Supervisor" />
                  </Field>
                </div>
                <div className="rounded-lg bg-orange-50 border border-orange-100 px-3 py-2">
                  <p className="text-xs text-orange-700">Contract workers can receive safety kits and be assigned to sites. They cannot log into the application.</p>
                </div>
                {contractError && <p className="text-red-600 text-sm">{contractError}</p>}
                <ModalActions onCancel={() => setContractCreateOpen(false)} submitLabel="Add Worker" />
              </form>
            </Modal>
          )}

          {contractEditTarget && (
            <Modal title={`Edit — ${contractEditTarget.name}`} onClose={() => setContractEditTarget(null)}>
              <form onSubmit={handleContractEdit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Employee ID *"><input className="input" value={contractEditForm.employeeId} onChange={(e) => setContractEditForm({ ...contractEditForm, employeeId: e.target.value })} required /></Field>
                  <Field label="Full Name *"><input className="input" value={contractEditForm.name} onChange={(e) => setContractEditForm({ ...contractEditForm, name: e.target.value })} required /></Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Email"><input className="input" type="email" value={contractEditForm.email} onChange={(e) => setContractEditForm({ ...contractEditForm, email: e.target.value })} placeholder="name@example.com" /></Field>
                  <Field label="Designation">
                    <input className="input" value={contractEditForm.role} onChange={(e) => setContractEditForm({ ...contractEditForm, role: e.target.value })} placeholder="e.g. Operator, Supervisor" />
                  </Field>
                </div>
                {contractError && <p className="text-red-600 text-sm">{contractError}</p>}
                <ModalActions onCancel={() => setContractEditTarget(null)} submitLabel="Save Changes" />
              </form>
            </Modal>
          )}

          {/* Delete Contract Worker Modal */}
          {contractDeleteCheck && (
            <Modal title={`Delete — ${contractDeleteCheck.worker.name}`} onClose={() => setContractDeleteCheck(null)}>
              {contractDeleteCheck.safe ? (
                <>
                  <p className="text-gray-700 mb-1">
                    Move <span className="font-semibold">"{contractDeleteCheck.worker.name}"</span> to Trash?
                  </p>
                  <p className="text-sm text-gray-500 mb-4">An administrator can restore this record later.</p>
                  {contractError && <p className="text-red-600 text-sm mb-3">{contractError}</p>}
                  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                    <button type="button" className="btn-secondary" onClick={() => setContractDeleteCheck(null)}>Cancel</button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                      onClick={() => handleContractDelete(false)}
                    >
                      Move to Trash
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">"{contractDeleteCheck.worker.name}"</span> still has {contractDeleteCheck.activeMaterials} active material(s) and {contractDeleteCheck.activeDurables} active durable(s) not returned:
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-lg mb-3">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-1.5">Item</th>
                          <th className="text-right px-3 py-1.5">Qty</th>
                          <th className="text-right px-3 py-1.5">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractDeleteCheck.items.map((it) => (
                          <tr key={`${it.type}-${it.id}`} className="border-t border-gray-100">
                            <td className="px-3 py-1.5">{it.name}</td>
                            <td className="px-3 py-1.5 text-right">{it.quantity}</td>
                            <td className="px-3 py-1.5 text-right">₹{it.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    Deleting will deduct <span className="font-semibold">₹{contractDeleteCheck.totalDeductibleAmount.toFixed(2)}</span> from salary for the unreturned items, then move the worker to Trash.
                  </p>
                  {contractError && <p className="text-red-600 text-sm mb-3">{contractError}</p>}
                  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                    <button type="button" className="btn-secondary" onClick={() => setContractDeleteCheck(null)}>Cancel</button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                      onClick={() => handleContractDelete(true)}
                    >
                      Deduct &amp; Delete
                    </button>
                  </div>
                </>
              )}
            </Modal>
          )}

          {/* Excel Upload Results Modal */}
          {contractBulkResult && (
            <Modal title="Excel Upload Results" onClose={() => setContractBulkResult(null)}>
              <div className="space-y-3">
                <div className="flex gap-4 text-sm flex-wrap">
                  <span className="text-green-700 font-medium">{contractBulkResult.created} created</span>
                  <span className="text-blue-700 font-medium">{contractBulkResult.updated || 0} updated</span>
                  <span className="text-gray-500 font-medium">{contractBulkResult.unchanged || 0} unchanged</span>
                  <span className="text-red-600 font-medium">{contractBulkResult.failed} failed</span>
                  <span className="text-gray-400">{contractBulkResult.total} total row{contractBulkResult.total !== 1 ? 's' : ''}</span>
                </div>
                {contractBulkResult.failedRecords?.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Row</th>
                          <th className="text-left px-3 py-2">Identifier</th>
                          <th className="text-left px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractBulkResult.failedRecords.map((f, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-3 py-1.5">{f.row}</td>
                            <td className="px-3 py-1.5">{f.identifier}</td>
                            <td className="px-3 py-1.5 text-red-600">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
                <button className="btn-primary" onClick={() => setContractBulkResult(null)}>Close</button>
              </div>
            </Modal>
          )}

          <ConfirmDialog
            open={!!contractConfirm}
            title="Toggle Status"
            message={`${contractConfirm?.active ? 'Deactivate' : 'Activate'} contract worker "${contractConfirm?.name}"?`}
            onConfirm={async () => { await workerApi.toggleActive(contractConfirm.id); setContractConfirm(null); loadContract(); }}
            onCancel={() => setContractConfirm(null)}
          />
        </>
      )}

      {/* ── AUDIT LOGS (Super Admin only) ── */}
      {pageTab === 'audit' && me?.role === 'super_admin' && <UserManagementAuditLog />}
    </div>
  );
}


// ── Action Menu (⋯ dropdown) ─────────────────────────────────────────────────
function ActionMenu({ children }) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Menu height is only known once it's actually rendered, so measure right
  // after mount and flip upward if there isn't enough room below — this is
  // what keeps the menu fully reachable for rows near the bottom of the page.
  useLayoutEffect(() => {
    if (!open || !ref.current || !menuRef.current) return;
    const buttonRect = ref.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    setOpenUpward(spaceBelow < menuHeight + 12);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title="Actions"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          className={`absolute right-0 z-30 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 ${
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {Array.isArray(children)
            ? children.map((child, i) => child && <div key={i} onClick={() => setOpen(false)}>{child}</div>)
            : <div onClick={() => setOpen(false)}>{children}</div>
          }
        </div>
      )}
    </div>
  );
}

const ActionItem = ({ children, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
      danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-gray-400 text-xs">Page {page} of {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onChange(page - 1)} disabled={page === 1}
          className="px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
          ← Prev
        </button>
        {Array.from({ length: Math.min(total, 7) }, (_, i) => {
          const p = total <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= total - 3 ? total - 6 + i : page - 3 + i;
          return (
            <button key={p} onClick={() => onChange(p)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${p === page ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-gray-50'}`}>
              {p}
            </button>
          );
        })}
        <button onClick={() => onChange(page + 1)} disabled={page === total}
          className="px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div><label className="label">{label}</label>{children}</div>
);

const ALL_MODULES = [
  { key: 'materialwatch', label: 'MaterialWatch', description: 'Safety kit & stationery management' },
  { key: 'material_master', label: 'Material Master', description: 'Material & price master data, estimations and BOQ cost intelligence' },
  { key: 'task_manager', label: 'Task Manager', description: 'Task, project, and job tracking' },
];

// A non-super_admin can only grant module access they hold themselves — the
// backend enforces this too, this is just matching UX so the option isn't
// offered in the first place. Super Admin implicitly has every module.
const ModuleCheckboxes = ({ selected, toggle }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const grantable = isSuperAdmin
    ? ALL_MODULES
    : ALL_MODULES.filter((m) => (user?.moduleAccess || []).includes(m.key));

  return (
    <div className="space-y-2 border border-gray-200 rounded-md p-3">
      {grantable.length === 0 && (
        <p className="text-xs text-gray-400">You don't have access to any modules yourself, so you can't grant module access to others.</p>
      )}
      {grantable.map((m) => (
        <label key={m.key} className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" className="mt-0.5 rounded" checked={selected.includes(m.key)} onChange={() => toggle(m.key)} />
          <div>
            <p className="text-sm font-medium text-gray-800">{m.label}</p>
            {m.description && <p className="text-xs text-gray-400">{m.description}</p>}
          </div>
        </label>
      ))}
    </div>
  );
};

const Modal = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className={`bg-white rounded-xl shadow-xl p-6 w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

const ModalActions = ({ onCancel, submitLabel, danger = false }) => (
  <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className={danger ? 'btn-danger' : 'btn-primary'}>{submitLabel}</button>
  </div>
);
